import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { getStudyFileURL, reSubmitStudy, StudyStatusesEnum, uploadPDFReport } from "../api/study";
import MFileUpload from "../components/MFileUpload";
import MButtonWithConfirm from "../components/MButtonWithConfirm";
import { MButton } from "../components/MButton";
import { baseUrls } from "../routes";
import MModal from "../components/MModal";
import { Link } from "react-router-dom";
import { formatDateTimeToShow } from "../utils/utils";
import { Grid, ListItem, ListItemText } from "@mui/material";
import { PermissionsE, usePermissions } from "../context/usePermissions";
import { List } from '@mui/material';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { deleteStudy } from "../api/study"
import { WindowSharp } from "@mui/icons-material";
import MRadio from "../components/MRadio";
import { isAdmin, isSuperAdmin, useAuth } from "../context/useAuth";

enum FileType {
    SOURCE = 'source',
    PROCESSED = 'processed'
}

enum DataProcessingTypes {
    FAST = 'fast',
    FULL = 'full'
}

enum ReportProcessingTypes {
    PROCESS = 'process',
    SKIP = 'skip'
}


const processingDataOptions = [
    {
        "value": DataProcessingTypes.FAST,
        "label": <>
            <Typography sx={{ fontWeight: 900 }} variant="h5">
                Fast re-submission (Recommended)
            </Typography>
            <Typography variant="body2">
                Recommended option. System will skip raw files processing
            </Typography>
            <Typography sx={{ fontSize: "16px", color: "red", fontStyle: 'italic' }} variant="body2">
                Recommended in following cases: <br />
                * Error on AMPS/CER-SCor side(study appeared in AMPS/CER-SCor but not processible) <br />
                * The study has already been processed but needs to be re-processed
            </Typography>
            <br />
        </>
    },
    {
        "value": DataProcessingTypes.FULL,
        "label": <>
            <Typography sx={{ fontWeight: 900 }} variant="h5">
                Full Processing
            </Typography>
            <Typography variant="body2">
                Re-run the complete processing pipeline, including all raw files originally uploaded by the client.
            </Typography>
            <Typography sx={{ fontSize: "16px", color: "red", fontStyle: 'italic' }} variant="body2">
                Recommended in following cases: <br />
                * The start date has changed <br />
                * There was an error processing raw files on the Mawi side(ID Track is missing)
            </Typography>
            <br />
        </>
    }
]


const processingReportOptions = [
    {
        "value": ReportProcessingTypes.PROCESS,
        "label": <>
            <Typography sx={{ fontWeight: 900 }} variant="h5">
                No (Recommended)
            </Typography>
        </>
    },
    {
        "value": ReportProcessingTypes.SKIP,
        "label": <>
            <br />
            <Typography sx={{ fontWeight: 900 }} variant="h5">
                Yes
            </Typography>
            <Typography sx={{ fontSize: "16px", color: "red", fontStyle: 'italic' }} variant="body2">
                In this case newely generated report will be ingored. Use this for training/test purposes only
            </Typography>
        </>
    }

]

export const StudyActionsAdmin = (study: any) => {
    const { hasPermission } = usePermissions();
    const { user } = useAuth();

    const [PDFBase64, setPDFBas64] = useState<any>(null);
    const [isReSubmited, setIsResubmited] = useState(false);
    const [busy, setBusy] = useState(false);
    const [rawFilesList, setRawFilesList] = useState([]);
    // const [processedFilesList, setProcessedFilesList] = useState([]);
    const [isFileLinksModalOpen, setIsFileLinksModalOpen] = useState(false);
    const [selectedFileType, setSelectedFileType] = useState(FileType.SOURCE);


    const [isStudyReSubmissionModalOpen, setIsStudyReSubmissionModalOpen] = useState(false);
    const [isUpdateInDatabase, setIsUpdateInDatabase] = useState<boolean>(true);

    const [dataProcessingOption, setDataProcessingOption] = useState<string>(DataProcessingTypes.FAST);
    const [reportProcessingOption, setReportProcessingOption] = useState<string>(ReportProcessingTypes.PROCESS);

    const convertBase64 = (file: any) => {
        return new Promise((resolve, reject) => {
            const fileReader = new FileReader();
            fileReader.readAsDataURL(file)
            fileReader.onload = () => {

                let encoded = fileReader.result?.toString().replace(/^data:(.*,)?/, '');
                if (encoded) {
                    if ((encoded?.length % 4) > 0) {
                        encoded += '='.repeat(4 - (encoded.length % 4));
                    }
                    resolve(encoded);
                }

            }
            fileReader.onerror = (error) => {
                console.log(error);
                reject(error);
            }
        })
    }
    const handleFileRead = async (file: File | undefined) => {
        if (!file) {
            setPDFBas64(null);
            return;
        }
        const base64 = await convertBase64(file)
        setPDFBas64(base64);
    }

    const isAllowedStudyDeletion = () => {
        let is_status_allow = study?.status === StudyStatusesEnum.not_enrolled || study?.status === StudyStatusesEnum.inprogress;
        let is_admin = isAdmin(user) || isSuperAdmin(user);

        if (is_status_allow) {
            return true;
        }

        if (is_admin) {
            return true;
        }

        return false;
    }

    const uploadNewPDF = async (isSaveOriginal?: boolean) => {
        let id_track = null;
        if (study.id_track) {
            id_track = study.id_track
        } else {
            id_track = study.measurements[0].id_track
        }

        if (id_track) {
            setBusy(true);
            let data = {
                "file_base64": PDFBase64,
                "exam_reporting_request_id": id_track,
                "is_save_original": !!isSaveOriginal
            }
            await uploadPDFReport(data).finally(() =>
                setTimeout(() => {
                    setBusy(false);
                }, 500)
            );
            window.location.reload();
        }
    }

    const deleteStudyHandler = async () => {
        await deleteStudy(study.id);
        window.location.href = '/';
    }

    const sendStudyReSubmit = async () => {
        const shouldForceRawProcessing = dataProcessingOption === DataProcessingTypes.FULL;
        let data = {
            id_study: study.id,
            is_update_database: isUpdateInDatabase,
            is_force_raw_file_processing: shouldForceRawProcessing
        }
        await reSubmitStudy(data)
        setIsResubmited(true);
    }

    const getRawFiles = async (fileType: string) => {
        let data = await getStudyFileURL(study.id);
        let urls = data.data.urls;

        setRawFilesList(urls);


        setIsFileLinksModalOpen(true);
        setBusy(false);

    }



    const renderReSubmissionModal = (
        <MModal
            open={isStudyReSubmissionModalOpen}
            handleClose={() => {
                setIsStudyReSubmissionModalOpen(false);
            }}
            width={800}
            height={700}
        >
            <>
                <Box sx={{
                    width: "100%",
                    overflowY: 'auto', // Enables vertical scrolling
                    overflowX: 'hidden' // Prevents horizontal scrolling
                }}>
                    <Typography variant="h4" sx={{ mb: 4 }} paragraph={true}>
                        Re-submit Study for Analysis
                    </Typography>
                    <>
                        <Typography variant="h5" sx={{ mb: 2 }} paragraph={true}>
                            Processing options
                        </Typography>
                        <MRadio
                            label=""
                            options={processingDataOptions}
                            disabled={false}
                            value={dataProcessingOption}
                            onChange={(e, val) => setDataProcessingOption(val)}
                        />
                        <Typography variant="h5" sx={{ mb: 2, mt: 2 }} paragraph={true}>
                            <b>Prevent report overwriting</b>
                        </Typography>
                        <MRadio
                            label=""
                            options={processingReportOptions}
                            disabled={false}
                            value={reportProcessingOption}
                            onChange={(e, val) => {
                                setIsUpdateInDatabase(val === ReportProcessingTypes.PROCESS)
                                setReportProcessingOption(val)
                            }}
                        />
                    </>
                </Box>
                <Box sx={{ textAlign: "center", mt: 4 }}>
                    <MButton color="error"
                        busy={busy}
                        sx={{ ml: 2 }}
                        onClick={() => { setIsStudyReSubmissionModalOpen(false) }}>
                        Cancel
                    </MButton>
                    <MButton
                        busy={busy}
                        sx={{ ml: 2 }}
                        onClick={() => {
                            setBusy(true)
                            sendStudyReSubmit()
                            setBusy(false)
                            setIsStudyReSubmissionModalOpen(false)
                        }}>
                        Submit
                    </MButton>

                </Box>
            </>
        </MModal>
    )

    const modal = (
        <MModal
            open={isFileLinksModalOpen}
            handleClose={() => {
                setIsFileLinksModalOpen(false)
                setBusy(false);
            }}
            width={1250}
            height={600}
        >
            <Box p={3} sx={{
                width: "100%",
                overflowY: 'auto', // Enables vertical scrolling
                overflowX: 'hidden' // Prevents horizontal scrolling
            }}>
                <Typography variant="h4" sx={{ textAlign: "center", mb: 4 }} paragraph={true}>
                    Study raw files links
                </Typography>
                <>
                    <TableContainer component={Paper} sx={{
                        mt: 4,
                        overflowY: 'auto', // Enables vertical scrolling
                        overflowX: 'hidden', // Prevents horizontal scrolling
                        width: "100%"
                    }}>
                        <Table sx={{ mt: 4, width: "100%" }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>File Name</TableCell>
                                    <TableCell>Size</TableCell>
                                    <TableCell>Created at</TableCell>
                                    <TableCell>Link Expires</TableCell>
                                    <TableCell>Link</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rawFilesList.map((url_element: any, index) => {
                                    const isPdf = url_element?.file_name?.toLowerCase().endsWith('.pdf');
                                    return (
                                        <TableRow key={index} sx={{ width: "100%" }}>
                                            <TableCell>{url_element?.file_name}</TableCell>
                                            <TableCell>{(url_element?.file_size) ? url_element?.file_size.toFixed(3) : "?"} mb</TableCell>
                                            <TableCell>
                                                {(url_element?.date_created) ? url_element?.date_created : "?"}
                                            </TableCell>
                                            <TableCell>
                                                {formatDateTimeToShow(new Date(Date.now() + url_element?.expires_on * 1000))}
                                            </TableCell>
                                            <TableCell>
                                                <a
                                                    href={url_element?.link}
                                                    download={!isPdf}
                                                    target={isPdf ? '_blank' : '_self'}
                                                    rel={isPdf ? 'noopener noreferrer' : ''}
                                                >
                                                    {isPdf ? 'Open PDF' : 'Save file'}
                                                </a>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>

                <Box sx={{ textAlign: "center", mt: 4 }}>
                    <MButton busy={busy} sx={{ ml: 2 }} onClick={() => { setIsFileLinksModalOpen(false) }}>
                        Close
                    </MButton>
                </Box>
            </Box>
        </MModal>
    );

    return (
        <>
            {renderReSubmissionModal}
            {modal}
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="h4" fontStyle={{ "color": "#4F4949" }}>Study actions</Typography>
                </Grid>
                {
                    hasPermission!(PermissionsE["study.rep_replace"]) ? (
                        <Grid item xs={5}>
                            <Typography variant="h5">Replace report: </Typography>
                            <Box my={3}>
                                <MFileUpload
                                    inputProps={{ accept: '.pdf' }}
                                    onFileChangeHandler={f => handleFileRead(f)}
                                />
                            </Box>
                            {
                                PDFBase64 && (
                                    <Box>
                                        <Box mb={2}>
                                            <MButtonWithConfirm
                                                busy={busy}
                                                confirmHandler={() => uploadNewPDF(true)}
                                                confirmContent={
                                                    <Box mb={2}>
                                                        <Typography variant="h4">Do you want save without changes?</Typography>
                                                    </Box>
                                                }
                                            >
                                                Save without changes
                                            </MButtonWithConfirm>
                                        </Box>
                                        <Box>
                                            <MButtonWithConfirm
                                                busy={busy}
                                                confirmHandler={() => uploadNewPDF()}
                                                confirmContent={
                                                    <Box mb={2}>
                                                        <Typography variant="h4">Do you want process report?</Typography>
                                                    </Box>
                                                }
                                            >
                                                Process (remove cover page, add meta data etc.)
                                            </MButtonWithConfirm>
                                        </Box>
                                    </Box>
                                )
                            }
                        </Grid>
                    ) : <></>
                }


                {
                    hasPermission!(PermissionsE["study.submission"]) ? (
                        <Grid item xs={4}>
                            {!isReSubmited && (
                                <Box>
                                    <Typography variant="h5">Re-submit study for analysis</Typography>
                                    <Box mt={3}>
                                        <MButton
                                            onClick={() => setIsStudyReSubmissionModalOpen(true)}
                                        >
                                            Re-submit study
                                        </MButton>
                                    </Box>
                                </Box>
                            )}
                        </Grid>
                    ) : <></>
                }
                {
                    hasPermission!(PermissionsE["study.raw"]) ? (
                        <Grid item xs={3}>
                            <Typography variant="h5">Study file links</Typography>
                            <Box mt={3} display="flex" flexDirection="column" sx={{ width: 160 }}>
                                <MButton
                                    disabled={busy}
                                    onClick={(e) => {
                                        setBusy(true);
                                        setSelectedFileType(FileType.SOURCE)
                                        // setIsFileLinksModalOpen(true);
                                        getRawFiles(FileType.SOURCE)
                                    }}
                                >
                                    Raw files
                                </MButton>
                            </Box>
                        </Grid>
                    ) : <></>
                }

            </Grid>
            <Grid container spacing={2}>
                {
                    hasPermission!(PermissionsE["study.w_meta"]) && isAllowedStudyDeletion() ? (
                        <Grid item xs={12}>
                            <Box>
                                <Typography variant="h5">Study deletion</Typography>
                                <Typography variant="subtitle2">Only for studies in status "In progress" or "Not enrolled"</Typography>
                                <Box mt={3} display="flex" flexDirection="column" sx={{ width: 160 }}>
                                    <MButtonWithConfirm
                                        busy={busy}
                                        color="error"
                                        confirmHandler={() => deleteStudyHandler()}
                                        confirmContent={
                                            <Box mb={4}>
                                                <Typography variant="h4">Delete study?</Typography>
                                            </Box>
                                        }
                                    >
                                        Delete Study
                                    </MButtonWithConfirm>
                                </Box>
                            </Box>
                        </Grid>
                    ) : <></>
                }
            </Grid>
        </>

    )
}
